import React from "react";
import { Grid, Typography, AccordionDetails } from "@material-ui/core";
import { v4 } from "uuid";
import { useStyles } from "./styles";
import { translate as t } from "../../../../../../../infrastructure/translate/localization";
import { MonetaryAmount } from "../../../../../../domain/monetary-amount";
import { TrainingInfo } from "../../../../../../domain/RecurrentPayment";

interface AccordionData {
  internalId?: string;
  legalId: string;
  email: string;
  trainingInfo: TrainingInfo;
}

function TrainingInfoDisplay({ trainingInfo }: { trainingInfo: TrainingInfo }) {
  const { extraEmployeeLabel, extraEmployeeData } = useStyles({
    borderType: "",
    labelStyle: "",
    dataStyle: "",
  });
  if (!trainingInfo) return null;
  return (
    trainingInfo && (
      <>
        <Grid container>
          <Typography className={extraEmployeeLabel}>
            {t("recurrent-payments-section.init-date")}
          </Typography>
          <Typography className={extraEmployeeData}>
            {trainingInfo.startDate}
          </Typography>
        </Grid>
        <Grid container>
          <Typography className={extraEmployeeLabel}>
            {t("recurrent-payments-section.end-date")}
          </Typography>
          <Typography className={extraEmployeeData}>
            {trainingInfo.endDate}
          </Typography>
        </Grid>
      </>
    )
  );
}

export default function DataRow({
  label,
  data,
  subLabel,
  borderType,
  labelStyle,
  dataStyle,
  hasAccordion,
  accordionData,
  hasBreakdown,
  breakdownData,
}: {
  label: string;
  data: string;
  subLabel: string;
  borderType: string;
  labelStyle: string;
  dataStyle: string;
  hasAccordion: boolean;
  accordionData: AccordionData;
  hasBreakdown: boolean;
  breakdownData: any;
}): JSX.Element {
  const {
    row,
    labelInfo,
    dataInfo,
    subLabelInfo,
    container,
    dataContainer,
    breakdownContainer,
    extraEmployeeLabel,
    extraEmployeeData,
    extraLabel,
    extraData,
  } = useStyles({
    borderType,
    labelStyle,
    dataStyle,
  });

  return (
    <Grid className={row}>
      {hasAccordion ? (
        <Grid>
          <Grid container id="panel1a-header">
            <Typography>{label}</Typography>
            <Typography className={dataInfo}>{data}</Typography>
          </Grid>
          <AccordionDetails>
            <Grid className={container}>
              <Grid container>
                <Typography className={extraEmployeeLabel}>
                  {t("common-messages.internal-id")}
                </Typography>
                <Typography className={extraEmployeeData}>
                  {accordionData.internalId}
                </Typography>
              </Grid>
              <Grid container>
                <Typography className={extraEmployeeLabel}>DNI/NIE</Typography>
                <Typography className={extraEmployeeData}>
                  {accordionData.legalId}
                </Typography>
              </Grid>
              <Grid container>
                <Typography className={extraEmployeeLabel}>Email</Typography>
                <Typography className={extraEmployeeData}>
                  {accordionData.email}
                </Typography>
              </Grid>
              <TrainingInfoDisplay trainingInfo={accordionData.trainingInfo} />
            </Grid>
          </AccordionDetails>
        </Grid>
      ) : (
        <Grid container>
          <Typography className={labelInfo}>{label}</Typography>
          {subLabel ? (
            <Grid className={dataContainer}>
              <Typography align="right" className={dataInfo}>
                {data}
              </Typography>
              <Typography className={subLabelInfo}>{subLabel}</Typography>
            </Grid>
          ) : (
            <Typography className={dataInfo}>{data}</Typography>
          )}
        </Grid>
      )}
      {hasBreakdown && breakdownData !== null && (
        <Grid className={breakdownContainer}>
          <Grid container>
            <Typography className={extraLabel}>
              {breakdownData.modalityName}
            </Typography>
            <Typography className={extraData}>
              {`${breakdownData.modalityFee}€/${t("common-messages.month")}`}
            </Typography>
          </Grid>
          {breakdownData.complements.map((complement) => (
            <Grid container key={v4()}>
              <Typography
                className={extraLabel}
              >{`+ ${complement.name}`}</Typography>
              <Typography className={extraData}>
                {`${MonetaryAmount.ofCurrency({
                  amountInCents: complement.complementFee.amountInCents,
                  currency: complement.complementFee.currency,
                }).prettifyMoneyWithoutSymbol()}€/${t(
                  "common-messages.month"
                )}`}
              </Typography>
            </Grid>
          ))}
        </Grid>
      )}
    </Grid>
  );
}
